import React, { useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeftIcon, TrashIcon, PlayIcon, PauseIcon } from '@heroicons/react/24/outline'
import { api } from '../services/api'
import { useNotifications } from '../contexts/NotificationContext'
import LoadingSpinner from '../components/LoadingSpinner'
import FormattedMessage from '../components/FormattedMessage'

interface Message {
  role: 'user' | 'assistant'
  content: string
  timestamp: string
  formattedTime: string
}

interface ConversationDetail {
  id: string
  sessionId: string
  startedAt: string
  lastMessageAt: string
  endedAt?: string
  isActive: boolean
  messageCount: number
  duration: number
  ipAddress: string
  userAgent: string
  userInfo?: {
    name?: string
    email?: string
    phone?: string
  }
  source: string
  deviceType: string
  referrer?: string
  messages: Message[]
}

const ConversationDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const { markAsRead, notifications } = useNotifications()

  const { data: conversation, isLoading, error } = useQuery<ConversationDetail>({
    queryKey: ['conversation', id],
    queryFn: () => api.get(`/conversations/${id}`).then((res: any) => res.data),
    refetchInterval: 2000, // Refetch every 2 seconds for real-time updates
  })

  // Auto-scroll to latest message
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  // Scroll to bottom when messages change
  useEffect(() => {
    if (conversation?.messages?.length) {
      scrollToBottom()
    }
  }, [conversation?.messages])

  // Mark notifications as read when viewing conversation
  useEffect(() => {
    if (id) {
      const conversationNotifications = notifications.filter(
        n => n.conversationId === id && !n.read
      )
      conversationNotifications.forEach(notification => {
        markAsRead(notification.id)
      })
    }
  }, [id, notifications, markAsRead])

  const updateStatusMutation = useMutation({
    mutationFn: (isActive: boolean) =>
      api.patch(`/conversations/${id}/status`, { isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversation', id] })
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/conversations/${id}`),
    onSuccess: () => {
      navigate('/conversations')
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
    },
  })

  const handleStatusToggle = () => {
    if (conversation) {
      updateStatusMutation.mutate(!conversation.isActive)
    }
  }

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this conversation?')) {
      deleteMutation.mutate()
    }
  }

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}m ${remainingSeconds}s`
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString()
  }

  const getDeviceIcon = (deviceType: string) => {
    switch (deviceType.toLowerCase()) {
      case 'mobile': return '📱'
      case 'tablet': return '📱'
      case 'desktop': return '💻'
      default: return '🖥️'
    }
  }

  const getSourceBadge = (source: string) => {
    const colors = {
      direct: 'bg-primary-100 text-primary-800',
      website: 'bg-green-100 text-green-800',
      widget: 'bg-purple-100 text-purple-800',
      api: 'bg-orange-100 text-orange-800',
    }
    return colors[source as keyof typeof colors] || 'bg-muted text-foreground'
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  if (error || !conversation) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Failed to load conversation details.</p>
          <button
            onClick={() => navigate('/conversations')}
            className="mt-2 btn-secondary"
          >
            Back to Conversations
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/conversations')}
            className="btn-secondary btn-sm flex items-center space-x-2"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            <span>Back</span>
          </button>
          <h1 className="text-2xl font-semibold text-foreground">
            Conversation Details
          </h1>
          <div className="flex items-center space-x-2">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              conversation.isActive
                ? 'bg-green-100 text-green-800'
                : 'bg-muted text-foreground'
            }`}>
              {conversation.isActive ? '● Active' : '● Inactive'}
            </span>
            {conversation.isActive && (
              <span className="text-xs text-green-600 animate-pulse">Live</span>
            )}
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={handleStatusToggle}
            disabled={updateStatusMutation.isPending}
            className={`btn-sm flex items-center space-x-2 ${
              conversation.isActive ? 'btn-secondary' : 'btn-success'
            }`}
          >
            {conversation.isActive ? (
              <>
                <PauseIcon className="h-4 w-4" />
                <span>Deactivate</span>
              </>
            ) : (
              <>
                <PlayIcon className="h-4 w-4" />
                <span>Activate</span>
              </>
            )}
          </button>
          <button
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            className="btn-danger btn-sm flex items-center space-x-2"
          >
            <TrashIcon className="h-4 w-4" />
            <span>Delete</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Conversation Info */}
        <div className="lg:col-span-1">
          <div className="card space-y-6">
            <h2 className="text-lg font-medium text-foreground">Information</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-muted-foreground">Session ID</label>
                <p className="mt-1 text-sm text-foreground font-mono break-all">{conversation.sessionId}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-muted-foreground">Started</label>
                <p className="mt-1 text-sm text-foreground">{formatDate(conversation.startedAt)}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-muted-foreground">Last Message</label>
                <p className="mt-1 text-sm text-foreground">{formatDate(conversation.lastMessageAt)}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-muted-foreground">Duration</label>
                <p className="mt-1 text-sm text-foreground">{formatDuration(conversation.duration)}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-muted-foreground">Message Count</label>
                <p className="mt-1 text-sm text-foreground">{conversation.messageCount}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-muted-foreground">IP Address</label>
                <p className="mt-1 text-sm text-foreground font-mono">{conversation.ipAddress}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-muted-foreground">Source</label>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSourceBadge(conversation.source)}`}>
                  {conversation.source}
                </span>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-muted-foreground">Device</label>
                <p className="mt-1 text-sm text-foreground flex items-center space-x-2">
                  <span>{getDeviceIcon(conversation.deviceType)}</span>
                  <span>{conversation.deviceType}</span>
                </p>
              </div>
              
              {conversation.userInfo && (conversation.userInfo.name || conversation.userInfo.email || conversation.userInfo.phone) && (
                <>
                  <div className="border-t pt-4">
                    <h4 className="text-sm font-medium text-foreground mb-3">User Information</h4>
                  </div>
                  {conversation.userInfo.name && (
                    <div>
                      <label className="block text-sm font-medium text-muted-foreground">Name</label>
                      <p className="mt-1 text-sm text-foreground">{conversation.userInfo.name}</p>
                    </div>
                  )}
                  {conversation.userInfo.email && (
                    <div>
                      <label className="block text-sm font-medium text-muted-foreground">Email</label>
                      <p className="mt-1 text-sm text-foreground">{conversation.userInfo.email}</p>
                    </div>
                  )}
                  {conversation.userInfo.phone && (
                    <div>
                      <label className="block text-sm font-medium text-muted-foreground">Phone</label>
                      <p className="mt-1 text-sm text-foreground">{conversation.userInfo.phone}</p>
                    </div>
                  )}
                </>
              )}
              
              {conversation.referrer && (
                <div>
                  <label className="block text-sm font-medium text-muted-foreground">Referrer</label>
                  <p className="mt-1 text-sm text-foreground break-all">{conversation.referrer}</p>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-muted-foreground">User Agent</label>
                <p className="mt-1 text-xs text-muted-foreground break-all">{conversation.userAgent}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="lg:col-span-2">
          <div className="card">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-medium text-foreground">Messages</h2>
              <span className="text-sm text-muted-foreground">
                {conversation.messages.length} messages
              </span>
            </div>
            
            <div className="space-y-4 max-h-[600px] overflow-y-auto">
              {conversation.messages.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">No messages in this conversation yet.</p>
                </div>
              ) : (
                <>
                  {conversation.messages.map((message, index) => (
                    <div
                      key={index}
                      className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-lg p-4 ${
                          message.role === 'user'
                            ? 'bg-primary text-white'
                            : 'bg-muted text-foreground'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-xs font-medium ${
                            message.role === 'user' ? 'text-primary-100' : 'text-muted-foreground'
                          }`}>
                            {message.role === 'user' ? 'User' : 'Assistant'}
                          </span>
                          <span className={`text-xs ${
                            message.role === 'user' ? 'text-primary-200' : 'text-muted-foreground/70'
                          }`}>
                            {message.formattedTime}
                          </span>
                        </div>
                        {/* Assistant replies are Markdown; user text is shown
                            verbatim so typed asterisks aren't reinterpreted. */}
                        {message.role === 'user' ? (
                          <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                        ) : (
                          <FormattedMessage content={message.content} className="text-sm" />
                        )}
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>
            
            {conversation.isActive && (
              <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center space-x-2">
                  <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-sm text-green-800">Conversation is active - updates automatically</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ConversationDetails